import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Loader2, 
  Check, 
  ShoppingCart, 
  Zap,
  ArrowRight,
  Mail,
  Lock,
  User
} from 'lucide-react';

const UpgradeSignup = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, register } = useAuth();
  
  // Get return URL for after signup (e.g., the grocery list they were viewing)
  const returnTo = searchParams.get('returnTo') || '/grocery-lists';
  
  const [isLogin, setIsLogin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        // Login flow - use AuthContext login
        await login(formData.email, formData.password);
        toast.success('Welcome back!');
      } else {
        // Signup flow - use AuthContext register
        await register(formData.email, formData.password, formData.name);
        toast.success('Account created!');
      }
      
      // Redirect to subscription page to complete upgrade
      navigate(`/subscription?upgrade=true&returnTo=${encodeURIComponent(returnTo)}`);
    } catch (error) {
      let message = 'Something went wrong. Please try again.';
      if (error.response?.status === 422) {
        message = 'Invalid email format. Please check your email address.';
      } else if (error.response?.status === 400 && error.response?.data?.detail?.includes('already exists')) {
        message = 'This email is already registered. Try signing in instead.';
      } else if (error.response?.status === 401) {
        message = 'Invalid email or password.';
      } else if (error.response?.data?.detail) {
        message = error.response.data.detail;
      }
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4" data-testid="upgrade-signup-page">
      <div className="w-full max-w-md">
        {/* Progress indicator */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30">
            <ShoppingCart className="h-3 w-3 mr-1" />
            Step 4.5
          </Badge>
          <span className="text-sm text-muted-foreground">Almost there!</span>
        </div>

        <Card className="border-border/50 shadow-lg">
          <CardContent className="pt-8 pb-6 px-6">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Zap className="h-7 w-7 text-primary" />
              </div>
              <h1 className="text-2xl font-semibold font-['Playfair_Display'] mb-2">
                {isLogin ? 'Sign in to upgrade' : 'Create your account to finish your plan'}
              </h1>
              <p className="text-muted-foreground">
                You&apos;re one step away from sending your grocery list to Instacart and planning meals without limits.
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="name"
                      type="text"
                      placeholder="Your name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="pl-10"
                      required={!isLogin}
                      data-testid="upgrade-name-input"
                    />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="pl-10"
                    required
                    data-testid="upgrade-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    placeholder={isLogin ? "Your password" : "Create a password"}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="pl-10"
                    required
                    minLength={6}
                    data-testid="upgrade-password-input"
                  />
                </div>
              </div>

              {/* Reassurance */}
              {!isLogin && (
                <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground py-2">
                  <span className="flex items-center gap-1">
                    <Check className="h-3 w-3 text-primary" />
                    Free to create
                  </span>
                  <span className="flex items-center gap-1">
                    <Check className="h-3 w-3 text-primary" />
                    Cancel anytime
                  </span>
                </div>
              )}

              {/* Submit Button */}
              <Button 
                type="submit" 
                className="w-full" 
                size="lg"
                disabled={loading}
                data-testid="upgrade-submit-btn"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    {isLogin ? 'Sign in & upgrade' : 'Create account & upgrade'}
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </>
                )}
              </Button>
            </form>

            {/* Toggle Login/Signup */}
            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={() => setIsLogin(!isLogin)}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                data-testid="toggle-auth-mode"
              >
                {isLogin ? (
                  <>Don&apos;t have an account? <span className="text-primary font-medium">Create one</span></>
                ) : (
                  <>Already have an account? <span className="text-primary font-medium">Sign in</span></>
                )}
              </button>
            </div>

            {/* What happens next */}
            <div className="mt-8 pt-6 border-t border-border">
              <p className="text-xs text-muted-foreground text-center mb-3">What happens next:</p>
              <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <div className="h-5 w-5 rounded-full bg-primary/20 flex items-center justify-center text-[10px] font-medium text-primary">1</div>
                  Create account
                </span>
                <ArrowRight className="h-3 w-3" />
                <span className="flex items-center gap-1">
                  <div className="h-5 w-5 rounded-full bg-primary/20 flex items-center justify-center text-[10px] font-medium text-primary">2</div>
                  Choose plan
                </span>
                <ArrowRight className="h-3 w-3" />
                <span className="flex items-center gap-1">
                  <div className="h-5 w-5 rounded-full bg-primary/20 flex items-center justify-center text-[10px] font-medium text-primary">3</div>
                  Export groceries
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Back link */}
        <div className="mt-6 text-center">
          <Link 
            to={`${returnTo}?demo=true`}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            ← Back to grocery list
          </Link>
        </div>
      </div>
    </div>
  );
};

export default UpgradeSignup;
